import moment from "moment/moment";
import React, { FC, Fragment } from "react";
import { IntlShape, defineMessages } from "react-intl";

import { countriesWithUSFormat, dayMonthFormat } from "shared/lib/datetime";
import { capitalize } from "shared/lib/functions";
import { getRoomsFromUrl } from "shared/lib/hotels/getRoomsFromUrl";
import { isFalsy } from "shared/lib/isFalsy";
import { formattedMessageParams } from "shared/messages";

import Img from "@components/FileServer/Img";
import { formatCityLabel } from "@components/SearchForm/formatCityLabel";
import FormattedMessage from "@components/UI/FormattedMessage";

import { calculateTotalTravelers } from "../../lib/Hotels/calculateTotalTravelers";

type MinimizedHeaderSearchFormProps = {
  from: {
    label: string;
    value: string;
    ShowCode?: boolean;
  };
  to: {
    label: string;
    value: string;
    ShowCode?: boolean;
  };
  departureDate: string;
  returnDate?: string;
  currentLanguage: string;
  defaultCountry: string;
  flightClass: string;
  intl?: IntlShape;
  onButtonClick: () => void;
  typeSearch: string;
  searchFlights: SearchFlight[];
  onToLinkClick: () => void;
  onFromLinkClick: () => void;
  passengersCount: number;
  query?: { [key: string]: string | string[] };
  isCbtEnabled?: boolean;
  cbtPassengersForm?: {
    hotelRoomAssignments: {
      [key: number]: {
        [key: string]: number;
      };
    };
  };
};

type LocationValue = {
  label: string;
  value: string;
  ShowCode?: boolean;
};

type SearchFlight = {
  origin: LocationValue;
  destination: LocationValue;
  departureDate: string;
};

const {
  propertiesSelectClassEconomy,
  propertiesSelectClassPremiumEconomy,
  propertiesSelectClassBusiness,
  propertiesSelectClassFirstClass,
  generalMore,
} = defineMessages({
  propertiesSelectClassEconomy: formattedMessageParams(
    "SearchForm.classEconomy"
  ),
  propertiesSelectClassPremiumEconomy: formattedMessageParams(
    "SearchForm.classPremiumEconomy"
  ),
  propertiesSelectClassBusiness: formattedMessageParams(
    "SearchForm.classBusiness"
  ),
  propertiesSelectClassFirstClass: formattedMessageParams(
    "SearchForm.classFirstClass"
  ),
  generalMore: formattedMessageParams("General.more"),
});

const MinimizedHeaderSearchForm: FC<MinimizedHeaderSearchFormProps> = ({
  from,
  to,
  departureDate,
  currentLanguage,
  defaultCountry,
  returnDate,
  flightClass,
  intl,
  onButtonClick,
  typeSearch,
  searchFlights,
  onToLinkClick,
  onFromLinkClick,
  passengersCount,
  query,
  isCbtEnabled,
  cbtPassengersForm,
}) => {
  const isReturnSearch = typeSearch === "RETURN";
  const isHotelSearch = typeSearch === "HOTELS";

  const rooms = getRoomsFromUrl(query, isCbtEnabled);
  const hotelRoomCbtAssignments = cbtPassengersForm?.hotelRoomAssignments || {};

  return (
    <div className="minimized-header-search-desktop">
      <div className="minimized-header-search-desktop__container">
        <div className="minimized-header-search-desktop__locations">
          <LocationsPart
            from={from}
            to={to}
            typeSearch={typeSearch}
            intl={intl}
            searchFlights={searchFlights}
            onFromClick={onFromLinkClick}
            onToClick={onToLinkClick}
            isReturnSearch={isReturnSearch}
          />
        </div>
        <div className="minimized-header-search-desktop__change-button-container">
          <button
            className="button-main-change"
            type="button"
            onClick={onButtonClick}
          >
            <FormattedMessage id="SearchForm.change" />
          </button>
        </div>
        <div className="minimized-header-search-desktop__dates">
          <DatesPart
            departureDate={departureDate}
            returnDate={returnDate}
            defaultCountry={defaultCountry}
            currentLanguage={currentLanguage}
            typeSearch={typeSearch}
            searchFlights={searchFlights}
          />
        </div>
        <div className="minimized-header-search-desktop__options">
          <OptionsPart
            flightClass={flightClass}
            intl={intl}
            typeSearch={typeSearch}
            paxNationality={
              isHotelSearch ? (query?.paxNationality as string) : undefined
            }
            rooms={isHotelSearch ? rooms?.length : undefined}
            passengersCount={
              isHotelSearch
                ? calculateTotalTravelers(rooms, hotelRoomCbtAssignments)
                : passengersCount
            }
          />
        </div>
      </div>
    </div>
  );
};

const LocationsPart: FC<{
  from: LocationValue;
  to: LocationValue;
  typeSearch: string;
  intl?: IntlShape;
  searchFlights: SearchFlight[];
  onFromClick: () => void;
  onToClick: () => void;
  isReturnSearch: boolean;
}> = ({
  from,
  to,
  typeSearch,
  intl,
  searchFlights,
  onFromClick,
  onToClick,
  isReturnSearch,
}) => {
  const isMultipleFlight =
    typeSearch === "MULTIPLE" && searchFlights.length > 1;
  const isHotelSearch = typeSearch === "HOTELS";

  return (
    <>
      <TypeSearchIcon typeSearch={typeSearch} isReturnSearch={isReturnSearch} />{" "}
      <span
        role="button"
        id="from-value"
        className="minimized-header-search-desktop__from-value"
      >
        {!isHotelSearch && (
          <>
            <span
              role="button"
              onClick={onFromClick}
              className="minimized-header-flight-segment"
            >
              <span className="header-search-form-inner-field-value">
                {formatCityLabel(
                  isMultipleFlight ? searchFlights[0].origin.label : from.label
                )}
              </span>{" "}
              <span className="header-search-form-inner-field-additional">
                {" "}
                {isFalsy(
                  isMultipleFlight
                    ? searchFlights[0].origin.ShowCode
                    : from.ShowCode
                )
                  ? ""
                  : `(${
                      isMultipleFlight
                        ? searchFlights[0].origin.value
                        : from.value
                    })`}
                {isMultipleFlight && ", "}
              </span>
            </span>
            {!isMultipleFlight && (
              <Img
                src="/static/images/ico-double-arrow.svg"
                alt="separator icon"
                className="minimized-header-search-form-locations-separator"
              />
            )}
          </>
        )}
        <span
          role="button"
          onClick={onToClick}
          className="minimized-header-flight-segment"
        >
          <span className="header-search-form-inner-field-value">
            {formatCityLabel(
              isMultipleFlight ? searchFlights[0].destination.label : to.label
            )}
          </span>{" "}
          <span className="header-search-form-inner-field-additional">
            {" "}
            {isFalsy(
              isMultipleFlight
                ? searchFlights[0].destination.ShowCode
                : to.ShowCode
            )
              ? ""
              : `(${
                  isMultipleFlight
                    ? searchFlights[0].destination.value
                    : to.value
                })`}
            {isMultipleFlight &&
              ` + ${searchFlights.length - 1} ${intl.formatMessage(
                generalMore
              )}`}
          </span>
        </span>
      </span>
    </>
  );
};

const weekday = ({
  date,
  currentLanguage,
  defaultCountry,
}: {
  date: string;
  currentLanguage: string;
  defaultCountry: string;
}) => {
  const isUSFormat = countriesWithUSFormat.includes(
    defaultCountry?.toLowerCase()
  );

  const result = capitalize(
    moment(new Date(`${date} 12:00`))
      .locale(currentLanguage)
      .format(isUSFormat ? "dddd" : "dd")
  );

  return result.substring(0, 3);
};

const OneWayFlightDatesPart: FC<{
  departureDate: string;
  defaultCountry: string;
  currentLanguage: string;
}> = ({ departureDate, defaultCountry, currentLanguage }) => (
  <span className="header-search-form-inner-field-dates" role="button">
    <span className="header-search-form-inner-field-value">
      {weekday({ date: departureDate, currentLanguage, defaultCountry })}
    </span>
    <span className="header-search-form-inner-field-additional">
      {moment(departureDate).format(dayMonthFormat(defaultCountry))}
    </span>
  </span>
);

const ReturnFlightDatesPart: FC<{
  departureDate: string;
  returnDate?: string;
  currentLanguage: string;
  defaultCountry: string;
}> = ({ departureDate, returnDate, currentLanguage, defaultCountry }) => (
  <span>
    <span className="header-search-form-inner-field-dates" role="button">
      <span className="header-search-form-inner-field-value">
        {weekday({ date: departureDate, currentLanguage, defaultCountry })}
      </span>
      <span className="header-search-form-inner-field-additional">
        {moment(departureDate).format(dayMonthFormat(defaultCountry))}
      </span>
    </span>
    <Img
      src="/static/images/ico-dash.svg"
      alt="separator icon"
      className="minimized-header-search-form-dates-separator"
    />
    <span className="header-search-form-inner-field-dates" role="button">
      <span className="header-search-form-inner-field-value">
        {weekday({ date: returnDate, currentLanguage, defaultCountry })}
      </span>
      <span className="header-search-form-inner-field-additional">
        {moment(returnDate).format(dayMonthFormat(defaultCountry))}
      </span>
    </span>
  </span>
);

const MultipleFlightDatesPart: FC<{
  defaultCountry: string;
  currentLanguage: string;
  searchFlights: SearchFlight[];
}> = ({ defaultCountry, currentLanguage, searchFlights }) => (
  <span>
    {searchFlights.map((flight, index) => {
      return (
        <Fragment key={index}>
          <span className="header-search-form-inner-field-dates" role="button">
            <span className="header-search-form-inner-field-value">
              {weekday({
                date: flight.departureDate,
                currentLanguage,
                defaultCountry,
              })}
            </span>
            <span className="header-search-form-inner-field-additional">
              {moment(flight.departureDate).format(
                dayMonthFormat(defaultCountry)
              )}
            </span>
          </span>
          {index < searchFlights.length - 1 && (
            <Img
              src="/static/images/ico-dash.svg"
              alt="separator icon"
              className="minimized-header-search-form-dates-separator"
            />
          )}
        </Fragment>
      );
    })}
  </span>
);
const DatesPart: FC<{
  departureDate: string;
  returnDate?: string;
  currentLanguage: string;
  defaultCountry: string;
  typeSearch: string;
  searchFlights: SearchFlight[];
}> = ({
  departureDate,
  returnDate,
  currentLanguage,
  defaultCountry,
  typeSearch,
  searchFlights,
}) => {
  const isMultipleFlight =
    typeSearch === "MULTIPLE" && searchFlights.length > 1;

  if (isMultipleFlight) {
    return (
      <MultipleFlightDatesPart
        defaultCountry={defaultCountry}
        currentLanguage={currentLanguage}
        searchFlights={searchFlights}
      />
    );
  }
  if (typeSearch === "ONE_WAY") {
    return (
      <OneWayFlightDatesPart
        departureDate={departureDate}
        defaultCountry={defaultCountry}
        currentLanguage={currentLanguage}
      />
    );
  }
  return (
    <ReturnFlightDatesPart
      departureDate={departureDate}
      returnDate={returnDate}
      defaultCountry={defaultCountry}
      currentLanguage={currentLanguage}
    />
  );
};

const OptionsPart: FC<{
  flightClass: string;
  intl?: IntlShape;
  typeSearch: string;
  passengersCount: number;
  rooms?: number | undefined;
  paxNationality?: string | undefined;
}> = ({
  passengersCount,
  flightClass,
  intl,
  typeSearch,
  rooms,
  paxNationality,
}) => {
  const isHotelSearch = typeSearch === "HOTELS";

  const flightClassTranslation = {
    ECO: {
      label: intl.formatMessage(propertiesSelectClassEconomy).toUpperCase(),
    },
    PRE: {
      label: intl
        .formatMessage(propertiesSelectClassPremiumEconomy)
        .toUpperCase(),
    },
    BUS: {
      label: intl.formatMessage(propertiesSelectClassBusiness).toUpperCase(),
    },
    "1ST": {
      label: intl.formatMessage(propertiesSelectClassFirstClass).toUpperCase(),
    },
  };
  return (
    <span>
      <Img
        src="/static/images/ico-person.svg"
        alt="person icon"
        className="minimized-header-search-desktop__person-icon"
      />
      <span role="button">
        <span className="header-search-form-inner-field-value">
          {passengersCount}
          {", "}
        </span>
        {isHotelSearch && rooms && (
          <span>
            <Img
              src="/static/images/ico-hotel-results.svg"
              alt="person icon"
              className="minimized-header-search-desktop__person-icon"
            />
            <span className="header-search-form-inner-field-value">
              {rooms}
              {", "}
            </span>
          </span>
        )}
        {isHotelSearch && (
          <span className="header-search-form-inner-field-additional">
            {paxNationality}
          </span>
        )}
        {!isHotelSearch && (
          <span className="header-search-form-inner-field-additional">
            {flightClassTranslation[flightClass]?.label.toLowerCase()}
          </span>
        )}
      </span>
    </span>
  );
};

const TypeSearchIcon: FC<{ typeSearch: string; isReturnSearch: boolean }> = ({
  typeSearch,
  isReturnSearch,
}) => {
  const isHotelSearch = typeSearch === "HOTELS";

  const typeSearchIconFilename = {
    ONE_WAY: "ico-plane-oneway-small.svg",
    RETURN: "ico-plane-back-small.svg",
    MULTIPLE: "ico-plane-double-small.svg",
  };

  if (isHotelSearch) {
    return <></>;
  }

  const iconClassName = `minimized-header-search-desktop__type-icon ${
    isReturnSearch ? "minimized-header-search-desktop__type-icon--return" : ""
  }`;

  return (
    <Img
      src={`/static/images/${typeSearchIconFilename[typeSearch]}`}
      alt="type search icon"
      className={iconClassName.trim()}
    />
  );
};

export default MinimizedHeaderSearchForm;
